// ============================================================================
// Positions Count analytics (Antonela spec §4.7). Right slide-out panel.
// All metrics computed client-side from the full requisition set:
//   * Open reqs by hospital — horizontal bar chart
//   * Open reqs by priority — donut (conic-gradient, dependency-free)
//   * Avg days open by stage — table
//   * Filled this month vs last calendar month — two KPI tiles + trend arrow
//
// "Open" = Open + Interview + On Hold (HIRING_OPEN_STAGES). Avg-days uses
// (current_date − created_date) for currently-open rows and
// (closed_date − created_date) for closed rows.
// ============================================================================

import { useMemo } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import {
  HIRING_OPEN_STAGES,
  HIRING_STAGES,
  HIRING_STAGE_LABELS,
  type HiringRequestStatus,
} from '../../shared/types';
import { RAG_COLORS } from '../../shared/design-tokens';
import type { HiringRequestRow } from './hooks/use-hiring-data';
import { overlayClickClose } from '../../shared/overlay-close';

interface Props {
  rows: HiringRequestRow[];
  onClose: () => void;
}

const PRIORITY_COLOR: Record<string, string> = {
  High: RAG_COLORS.Red,
  Medium: RAG_COLORS.Amber,
  Low: RAG_COLORS.Green,
};

export default function HiringAnalytics({ rows, onClose }: Props) {
  const m = useMemo(() => computeMetrics(rows), [rows]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-tcs-scrim/60"
      {...overlayClickClose(onClose)}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="max-h-screen w-full overflow-y-auto bg-tcs-overlay p-5 shadow-xl sm:w-[480px]"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-tcs-text">Positions count</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Close
          </button>
        </div>

        {/* KPI tiles */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <KpiTile
            label="Filled this month"
            value={m.filledThisMonth}
            prev={m.filledLastMonth}
            prevLabel="last month"
          />
          <KpiTile label="Open requisitions" value={m.openCount} />
        </div>

        {/* Open reqs by hospital — horizontal bars */}
        <Section title="Open reqs by hospital">
          {m.byHospital.length === 0 ? (
            <Empty />
          ) : (
            <ul className="space-y-1.5">
              {m.byHospital.map((h) => (
                <li key={h.name} className="text-xs">
                  <div className="mb-0.5 flex items-center justify-between">
                    <span className="truncate pr-2 text-tcs-text-secondary">{h.name}</span>
                    <span className="tabular-nums text-tcs-text-muted">{h.count}</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded bg-tcs-surface">
                    <div
                      className="h-full rounded bg-tcs-inverse"
                      style={{ width: `${(h.count / m.byHospitalMax) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Open reqs by priority — donut */}
        <Section title="Open reqs by priority">
          {m.openCount === 0 ? (
            <Empty />
          ) : (
            <div className="flex items-center gap-5">
              <Donut segments={m.priorityDonut} total={m.openCount} />
              <ul className="space-y-1 text-xs">
                {m.byPriority.map((p) => (
                  <li key={p.label} className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: PRIORITY_COLOR[p.label] }}
                    />
                    <span className="text-tcs-text-secondary">{p.label}</span>
                    <span className="tabular-nums text-tcs-text-muted">{p.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* Avg days open by stage — table */}
        <Section title="Avg days open by stage">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-tcs-border text-left text-tcs-text-muted">
                <th className="py-1 font-semibold">Stage</th>
                <th className="py-1 text-right font-semibold">Reqs</th>
                <th className="py-1 text-right font-semibold">Avg days</th>
              </tr>
            </thead>
            <tbody>
              {m.avgDaysByStage.map((s) => (
                <tr key={s.stage} className="border-b border-tcs-border last:border-0">
                  <td className="py-1 text-tcs-text-secondary">
                    {HIRING_STAGE_LABELS[s.stage]}
                  </td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-muted">
                    {s.count}
                  </td>
                  <td className="py-1 text-right tabular-nums text-tcs-text-secondary">
                    {s.count === 0 ? '—' : s.avgDays}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </aside>
    </div>
  );
}

interface Metrics {
  openCount: number;
  byHospital: { name: string; count: number }[];
  byHospitalMax: number;
  byPriority: { label: string; count: number }[];
  priorityDonut: { color: string; pct: number }[];
  avgDaysByStage: { stage: HiringRequestStatus; count: number; avgDays: number }[];
  filledThisMonth: number;
  filledLastMonth: number;
}

function computeMetrics(rows: HiringRequestRow[]): Metrics {
  const open = rows.filter((r) =>
    HIRING_OPEN_STAGES.includes((r.status ?? 'No_Status') as HiringRequestStatus),
  );

  // By hospital
  const hosp = new Map<string, number>();
  for (const r of open) {
    const name = r.account_name ?? r.account_id ?? 'Unassigned';
    hosp.set(name, (hosp.get(name) ?? 0) + 1);
  }
  const byHospital = Array.from(hosp, ([name, count]) => ({ name, count })).sort(
    (a, b) => b.count - a.count,
  );
  const byHospitalMax = byHospital.reduce((mx, h) => Math.max(mx, h.count), 1);

  // By priority
  const prio = { High: 0, Medium: 0, Low: 0 } as Record<string, number>;
  for (const r of open) {
    const p = r.urgency_label ?? 'Medium';
    prio[p] = (prio[p] ?? 0) + 1;
  }
  const byPriority = (['High', 'Medium', 'Low'] as const).map((label) => ({
    label,
    count: prio[label] ?? 0,
  }));
  const total = open.length || 1;
  const priorityDonut = byPriority.map((p) => ({
    color: PRIORITY_COLOR[p.label],
    pct: (p.count / total) * 100,
  }));

  // Avg days open by stage
  const now = Date.now();
  const avgDaysByStage = HIRING_STAGES.map((stage) => {
    const stageRows = rows.filter((r) => (r.status ?? 'No_Status') === stage);
    let sum = 0;
    let n = 0;
    for (const r of stageRows) {
      const start = r.created_date ?? r.created_at;
      if (!start) continue;
      const startT = Date.parse(start);
      if (!Number.isFinite(startT)) continue;
      const endT = r.closed_date ? Date.parse(r.closed_date) : now;
      const days = Math.max(0, Math.floor((endT - startT) / 86_400_000));
      sum += days;
      n += 1;
    }
    return {
      stage,
      count: stageRows.length,
      avgDays: n === 0 ? 0 : Math.round(sum / n),
    };
  });

  // Filled this month vs last calendar month
  const d = new Date();
  const thisMonthStart = new Date(d.getFullYear(), d.getMonth(), 1);
  const lastMonthStart = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  let filledThisMonth = 0;
  let filledLastMonth = 0;
  for (const r of rows) {
    if (r.status !== 'Filled' || !r.closed_date) continue;
    const t = new Date(r.closed_date + 'T00:00:00');
    if (t >= thisMonthStart) filledThisMonth += 1;
    else if (t >= lastMonthStart && t < thisMonthStart) filledLastMonth += 1;
  }

  return {
    openCount: open.length,
    byHospital,
    byHospitalMax,
    byPriority,
    priorityDonut,
    avgDaysByStage,
    filledThisMonth,
    filledLastMonth,
  };
}

function Donut({
  segments,
  total,
}: {
  segments: { color: string; pct: number }[];
  total: number;
}) {
  // Build a conic-gradient from the segment percentages.
  let acc = 0;
  const stops: string[] = [];
  for (const s of segments) {
    const start = acc;
    acc += s.pct;
    stops.push(`${s.color} ${start}% ${acc}%`);
  }
  const bg =
    stops.length > 0 ? `conic-gradient(${stops.join(', ')})` : '#e2e8f0';
  return (
    <div
      className="relative h-28 w-28 shrink-0 rounded-full"
      style={{ background: bg }}
    >
      <div className="absolute inset-[18%] flex flex-col items-center justify-center rounded-full bg-tcs-raised">
        <span className="text-lg font-semibold tabular-nums text-tcs-text">
          {total}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
          open
        </span>
      </div>
    </div>
  );
}

function KpiTile({
  label,
  value,
  prev,
  prevLabel,
}: {
  label: string;
  value: number;
  prev?: number;
  prevLabel?: string;
}) {
  const delta = prev === undefined ? null : value - prev;
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-tcs-text">
          {value}
        </span>
        {delta !== null && <Trend delta={delta} suffix={prevLabel} />}
      </div>
    </div>
  );
}

export function Trend({ delta, suffix }: { delta: number; suffix?: string }) {
  const Icon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  const color =
    delta > 0 ? 'text-st-ok-fg' : delta < 0 ? 'text-st-danger-fg' : 'text-tcs-text-muted';
  return (
    <span className={`flex items-center gap-0.5 text-xs ${color}`}>
      <Icon className="h-3.5 w-3.5" />
      <span className="tabular-nums">
        {delta > 0 ? '+' : ''}
        {delta}
      </span>
      {suffix && <span className="text-tcs-text-muted">vs {suffix}</span>}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-5">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Empty() {
  return <p className="text-xs text-tcs-text-muted">No open requisitions.</p>;
}
